import { ApplicationFeatures } from "@bezoned/shared/features/subscriptions/application-features.constants.js";
import { FeatureGuard } from "@bezoned/shared/features/subscriptions/feature-guard.jsx";
import { asStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import {
  Button,
  Dropdown,
  Field,
  Option,
  Spinner,
  Tooltip,
} from "@fluentui/react-components";
import type { ReactNode } from "react";

import { useSubscription } from "../../../../subscription/subscription.context.js";
import { useImageLoaded } from "../../../team-edit/tabs/theme/use-image-loaded.js";

interface OfficeDesignPreset {
  name: string;
  description: string | null;
  previewUrl: string | null;
  manifest: {
    tables: { id: string }[];
    meetingRooms: { id: string }[];
  };
}

interface ActionConfig {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  tooltip?: string;
}

interface TeamOfficeDesignSelectorProps {
  presets: { id: string; name: string }[];
  selectedPreset: OfficeDesignPreset | undefined;
  selectedPresetId: string | null;
  selectedPresetDisplayName: string;
  isDropdownDisabled: boolean;
  onPresetSelect: (presetId: string) => void;
  action: ActionConfig;
  configureAction?: ActionConfig;
  children?: ReactNode;
}

export type { OfficeDesignPreset };

const TooltipButton = ({
  action,
  appearance = "primary",
}: {
  action: ActionConfig;
  appearance?: "primary" | "secondary";
}) => {
  const isDisabled = action.disabled ?? false;
  const showTooltip = Boolean(action.tooltip) && isDisabled;

  const button = (
    <Button
      appearance={appearance}
      disabled={isDisabled}
      onClick={action.onClick}
      type="button"
    >
      {action.label}
    </Button>
  );

  if (!showTooltip) {
    return button;
  }

  return (
    <Tooltip content={action.tooltip ?? ""} relationship="description">
      {button}
    </Tooltip>
  );
};

export const TeamOfficeDesignSelector = ({
  presets,
  selectedPreset,
  selectedPresetId,
  selectedPresetDisplayName,
  isDropdownDisabled,
  onPresetSelect,
  action,
  configureAction,
  children,
}: TeamOfficeDesignSelectorProps) => {
  const previewUrl = selectedPreset?.previewUrl ?? null;
  const isImageLoaded = useImageLoaded(previewUrl);
  const tableCount = selectedPreset ? selectedPreset.manifest.tables.length : 0;
  const meetingRoomCount = selectedPreset
    ? selectedPreset.manifest.meetingRooms.length
    : 0;
  const selectedPresetDescription = selectedPreset?.description ?? null;
  const { features } = useSubscription();

  return (
    <Field label="Office design">
      <div className="flex flex-col gap-2">
        <div className="flex">
          <div className="flex w-1/4 min-w-0 flex-col justify-between pr-4">
            <div className="flex flex-col gap-3">
              <FeatureGuard
                availableFeatures={features}
                feature={ApplicationFeatures.OfficeDesignUpdate}
              >
                {(featureAvailable) => (
                  <Dropdown
                    button={{ className: "w-full !min-w-0" }}
                    className="w-full !min-w-0"
                    disabled={isDropdownDisabled || !featureAvailable}
                    onOptionSelect={asStableCallback((_event, data) => {
                      if (data.optionValue) {
                        onPresetSelect(data.optionValue);
                      }
                    })}
                    selectedOptions={selectedPresetId ? [selectedPresetId] : []}
                    value={selectedPresetDisplayName}
                  >
                    {presets.map((preset) => (
                      <Option key={preset.id} value={preset.id}>
                        {preset.name}
                      </Option>
                    ))}
                  </Dropdown>
                )}
              </FeatureGuard>
              {Boolean(selectedPreset) && (
                <div className="flex flex-col gap-1">
                  <p className="text-sm">
                    {tableCount} {tableCount === 1 ? "table" : "tables"}
                  </p>
                  <p className="text-sm">
                    {meetingRoomCount}{" "}
                    {meetingRoomCount === 1 ? "meeting room" : "meeting rooms"}
                  </p>
                </div>
              )}
              {configureAction && <TooltipButton action={configureAction} />}
            </div>
          </div>
          <div className="aspect-[4/3] w-3/4 overflow-hidden rounded bg-neutral-100">
            {previewUrl ? (
              <img
                alt={`Preview of ${selectedPreset?.name} office theme`}
                className={`h-full w-full object-contain transition-[filter] duration-500 ${isImageLoaded ? "" : "blur-md"}`}
                height={400}
                src={previewUrl}
                width={600}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <span className="text-sm text-neutral-400">No preview</span>
              </div>
            )}
          </div>
        </div>
        {selectedPresetDescription && (
          <p className="text-sm text-neutral-600">
            {selectedPresetDescription}
          </p>
        )}
        <div className="mt-2 flex items-center justify-end gap-2">
          {Boolean(action.isLoading) && <Spinner size="tiny" />}
          <TooltipButton action={action} />
        </div>
        {children}
      </div>
    </Field>
  );
};
